/**
 * THE ONE FULL SCREEN, document-wide.
 *
 * The architect's rule, 2026-09-19: "Any full screen need to allow normal workflow as though it
 * was not full screen. Any route changes etc should cancel it. Dialogs should be seen. Stacking
 * non allow. One full screen removes a previous". That is a rule about the DOCUMENT rather than
 * about any one component, so the mechanism that enforces it cannot live inside one: it was born
 * inside `agwa-fullscreen-block` and was lifted out here the moment a second component -
 * `agwa-lit-table`, which has its own full-screen mode - had to obey the same rule.
 *
 * NEITHER COMPONENT DEPENDS ON THE OTHER; BOTH DEPEND ON THIS. A leaf component such as the data
 * table must not import a layout component to be allowed to take the viewport, and a layout
 * component must not know which leaves exist. They meet at this module, which knows about neither
 * and only ever calls back through `FullscreenTakeoverHolder`.
 *
 * WHAT IT ENFORCES, one property per line of the rule:
 * - NO STACKING, AND ONE REMOVES A PREVIOUS. There is a single holder. Claiming while somebody
 *   else holds cancels them, so two takeovers can never be active at once however unrelated the
 *   two components are - and ancestry is never consulted, because two blocks nested in one another
 *   and two on opposite sides of a page are the same case to the document.
 * - ROUTE CHANGES CANCEL IT. A route that replaces the page disconnects the holder, which releases
 *   here. A route that only changes a tab segment does NOT disconnect anything: it sets
 *   `display: none` on a panel, which `watchForHiding` below is what catches.
 * - NORMAL WORKFLOW CONTINUES. Escape always gets a person out, and a key something above has
 *   already handled is left alone so an overlay closing itself does not also drop the page out of
 *   full screen.
 * - DIALOGS ARE SEEN. That one is not enforceable from here - it is each takeover's own stacking
 *   level, asserted where the stylesheets are (data-table.styles.css and
 *   agwa-fullscreen-block.styles.css both sit at 400, above the masthead and below every Shoelace
 *   overlay).
 */

/**
 * What this module needs of whatever is holding the takeover. It extends `Element` because the
 * holder is always a real element on the page: the hiding watcher walks its ancestors and asks it
 * whether it is still being rendered, neither of which can be done through an opaque handle.
 */
export interface FullscreenTakeoverHolder extends Element {
  /**
   * Leave full screen of the holder's own accord - the way out Escape takes. The holder is
   * expected to call `releaseTakeover` on itself as part of this.
   */
  collapseTakeover(): void;

  /**
   * Cancelled by somebody else claiming the takeover. The holder returns to the page and touches
   * NO global state: whoever displaced it owns the document lock and the key listener by then, and
   * releasing them here is precisely the two-holders-fighting bug this module exists to prevent.
   */
  yieldTakeover(): void;
}

/** THE ONE HOLDER, or null. Module scope, because the rule is about the document. */
let takeoverHolder: FullscreenTakeoverHolder | null = null;

/**
 * The document's own overflow from before the FIRST holder took over, restored when the LAST one
 * yields. Held here, beside the holder, because the lock is one piece of global state with one
 * owner: a per-instance copy is how two components come to fight over
 * `document.documentElement.style.overflow` and leave a page that cannot scroll. Remembered rather
 * than assumed empty, because the shell may legitimately set one.
 */
let documentOverflowBeforeTakeover = '';

/**
 * While somebody holds the takeover: the ancestors whose attributes decide whether the holder is
 * rendered at all, and the viewport it is meant to be covering. Null whenever nobody holds,
 * because there is then nothing to notice.
 */
let hidingWatcher: MutationObserver | null = null;
let viewportWatcher: IntersectionObserver | null = null;

/**
 * Escape is the way out that needs no aiming, and it belongs to whoever HOLDS the takeover - hence
 * one listener on `document` for the whole application rather than one per component, bound while
 * anybody holds it and dropped when nobody does. A key already handled by something above (a
 * Shoelace overlay closing itself) is left alone, or one Escape would both close a dialog raised
 * inside the takeover and drop the page out of it.
 */
const onDocumentKeydown = (event: KeyboardEvent): void => {
  if (event.key !== 'Escape' || event.defaultPrevented) return;
  takeoverHolder?.collapseTakeover();
};

/**
 * Take the takeover, cancelling whoever was holding it.
 *
 * The document lock and the Escape listener are taken only when there was NO holder, and are
 * therefore held exactly once however many components change hands: a takeover passing from one to
 * another must not release and re-take the lock, which would flash the page's scrollbar and
 * briefly hand Escape to nobody.
 *
 * THE DISPLACED HOLDER IS CANCELLED LAST, after the claim is complete, so the page behind never
 * becomes scrollable for an instant in between. It is cancelled HERE rather than by the caller
 * because forgetting to is silent - it leaves two takeovers drawn over one another, which is the
 * one thing the architect's rule forbids by name - and the second component to arrive is exactly
 * the one that would not have known to.
 */
export function claimTakeover(holder: FullscreenTakeoverHolder): void {
  const displaced = takeoverHolder;
  if (displaced === holder) return;
  if (!displaced) {
    documentOverflowBeforeTakeover = document.documentElement.style.overflow;
    document.documentElement.style.overflow = 'hidden';
    document.addEventListener('keydown', onDocumentKeydown);
  }
  takeoverHolder = holder;
  watchForHiding(holder);
  displaced?.yieldTakeover();
}

/**
 * Give the takeover up, if this element is the one holding it. The guard is what stops a holder
 * that was already CANCELLED by another from freeing the new holder's lock on its way out.
 */
export function releaseTakeover(holder: FullscreenTakeoverHolder): void {
  if (takeoverHolder !== holder) return;
  takeoverHolder = null;
  stopWatchingForHiding();
  document.removeEventListener('keydown', onDocumentKeydown);
  document.documentElement.style.overflow = documentOverflowBeforeTakeover;
}

/**
 * A HELD TAKEOVER MUST NOT SURVIVE ITS TAB, because `display: none` is not a disconnect.
 *
 * Open a product page's Reports tab, expand the LIST's own full screen, press browser Back. The
 * route changes the tab segment, `sl-tab-panel` loses its `active` attribute and its own stylesheet
 * hides it (`:host(:not([active])) { display: none }`), and the holder is still the holder:
 * `document.documentElement.style.overflow` stays `hidden` and the Escape listener stays bound,
 * with nothing on screen to explain why the page will not scroll. Escape recovers it and so does
 * claiming from anywhere else, so a person who tries neither has a dead page.
 *
 * IT BELONGS HERE AND NOT IN EACH COMPONENT. It is the same watcher, watching the same thing, for
 * the same reason, in every component that can take the viewport; the alternative is one copy per
 * component and a rule the next one has to remember. It is also what gives a takeover inside a tab
 * panel the architect's "any route changes etc should cancel it" - a tab-segment route change
 * disconnects nothing, so a disconnect hook alone would not see it.
 *
 * WHY THESE TWO WATCHERS, AND WHY NEITHER ALONE:
 * - `offsetParent === null` cannot be the check. CSSOM View returns null for EVERY element whose
 *   computed `position` is fixed, and a takeover is fixed exactly while it is held - so the test
 *   would be true the instant it opened and would close it again.
 * - The MutationObserver is the one that catches the real cause: what hides a tab panel is an
 *   ATTRIBUTE change on an ancestor. It is scoped to the holder's own ancestor chain, attributes
 *   only, and lives only while a takeover is held, so it is a handful of nodes seeing a change that
 *   happens when a person navigates.
 * - The IntersectionObserver covers a hide that arrives with no attribute behind it - a stylesheet
 *   swap, a container collapsing to nothing - which the chain walk cannot see.
 *
 * BOTH ASK THE SAME QUESTION and neither decides on its own: an IntersectionObserver reports a
 * first entry before the layout it describes exists, so acting on `isIntersecting` would collapse
 * a takeover a person had just opened. `checkVisibility()` is the one call that answers for an
 * ancestor's `display` as well as the element's own, and it is asked every time either fires.
 */
function watchForHiding(holder: FullscreenTakeoverHolder): void {
  stopWatchingForHiding();

  if (typeof MutationObserver !== 'undefined') {
    const watcher = new MutationObserver(() => collapseIfNotRendered());
    for (const ancestor of renderedAncestors(holder)) {
      watcher.observe(ancestor, { attributes: true });
    }
    hidingWatcher = watcher;
  }

  if (typeof IntersectionObserver !== 'undefined') {
    const watcher = new IntersectionObserver(() => collapseIfNotRendered());
    watcher.observe(holder);
    viewportWatcher = watcher;
  }
}

function stopWatchingForHiding(): void {
  hidingWatcher?.disconnect();
  hidingWatcher = null;
  viewportWatcher?.disconnect();
  viewportWatcher = null;
}

/**
 * The element and every element above it, hopping out of each shadow root by its host - a holder
 * is rendered inside a page's shadow DOM, so a walk that stopped at `parentNode` would stop one
 * node short of the tab panel that actually hides it.
 */
function* renderedAncestors(from: Element): Generator<Element> {
  let node: Node | null = from;
  while (node) {
    if (node instanceof Element) yield node;
    const parent: Node | null = node.parentNode;
    node = parent instanceof ShadowRoot ? parent.host : parent;
  }
}

/**
 * Give the takeover up if the holder is no longer being rendered. Collapsing rather than merely
 * releasing, so what comes back when its tab is shown again is the ordinary component a person
 * left, not an overlay nothing is holding.
 *
 * A browser without `checkVisibility` is treated as visible: refusing to answer must never close a
 * takeover somebody is using, and the unfixed behaviour is what it already had.
 */
function collapseIfNotRendered(): void {
  const holder = takeoverHolder;
  if (!holder) return;
  if (typeof holder.checkVisibility !== 'function') return;
  if (holder.checkVisibility()) return;
  holder.collapseTakeover();
}
